import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

type TourContextType = {
  activeTour: string | null
  enqueue: (tourName: string) => void
  release: (tourName: string) => void
}

const TourContext = createContext<TourContextType>({
  activeTour: null,
  enqueue: () => undefined,
  release: () => undefined,
})

export const TourProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const [activeTour, setActiveTour] = useState<string | null>(null)
  const queue = useRef<string[]>([])

  const enqueue = useCallback((tourName: string) => {
    setActiveTour((current) => {
      if (current === null) return tourName
      if (!queue.current.includes(tourName)) queue.current.push(tourName)
      return current
    })
  }, [])

  const release = useCallback((tourName: string) => {
    setActiveTour((current) => {
      queue.current = queue.current.filter((t) => t !== tourName)
      if (current !== tourName) return current
      return queue.current.shift() ?? null
    })
  }, [])

  const value = useMemo(
    () => ({ activeTour, enqueue, release }),
    [activeTour, enqueue, release],
  )

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

export const useTourManager = (): TourContextType => useContext(TourContext)
